import React, { useState } from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import type { GenericEntitiesProps, GenericEntity, GenericEntityItem } from './GenericEntities.types';
import { Button } from '../Button';
import { Table } from '../Table';
import { Input } from '../Input';
import { LayoutContent } from '../LayoutContent';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { TableColumn } from '../Table/Table.types';
import { GenericEntityModal } from './GenericEntityModal';

interface ModalState {
    isOpen: boolean;
    mode: 'create' | 'edit';
    entity?: GenericEntity | null;
    items: GenericEntityItem[];
}

export const GenericEntities: React.FC<GenericEntitiesProps> = ({
    title = 'Entidades Genéricas',
    entities = [],
    isLoading = false,
    onSelectEntity,
    onCreateEntity,
    onSaveEntity,
    onLoadItems,
    className = '',
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    
    // Unificamos el estado del modal de creación/edición
    const [modalState, setModalState] = useState<ModalState>({
        isOpen: false,
        mode: 'create',
        entity: null,
        items: []
    });
    
    const [isLoadingItems, setIsLoadingItems] = useState(false);

    const handleCreateClick = () => {
        setModalState({
            isOpen: true,
            mode: 'create',
            entity: null,
            items: []
        });
    };

    const handleSelectForEdit = async (entity: GenericEntity) => {
        if (onSelectEntity) onSelectEntity(entity);

        let loadedItems: GenericEntityItem[] = [];
        if (onLoadItems) {
            setIsLoadingItems(true);
            try {
                loadedItems = await onLoadItems(entity);
            } catch (err) {
                console.error('Error loading items:', err);
                loadedItems = [];
            } finally {
                setIsLoadingItems(false);
            }
        }

        setModalState({
            isOpen: true,
            mode: 'edit',
            entity,
            items: loadedItems
        });
    };

    const handleCloseModal = () => {
        setModalState(prev => ({ ...prev, isOpen: false }));
    };

    const handleSaveModal = (entityData: Partial<GenericEntity>, itemsData: GenericEntityItem[]) => {
        if (modalState.mode === 'create') {
            if (onCreateEntity) {
                // En modo creación asume que la interfaz de quien lo consume puede lidiar con las opciones
                // si la interfaz lo permite, o bien lo guarda todo en `onCreateEntity` o hace otra lógica.
                // Como `onCreateEntity` solo recibe Partial<GenericEntity> en los props actuales,
                // idealmente el consumidor debería tener una forma de recibir ambos, o nosotros llamamos a onSaveEntity si está creado.
                // Ajustamos para usar onSaveEntity si se provee, o onCreateEntity como estaba.
                if (onSaveEntity && entityData.id) {
                     onSaveEntity(entityData as GenericEntity, itemsData);
                } else if (onCreateEntity) {
                     onCreateEntity(entityData, itemsData);
                     // Nota: Si se requiere guardar items en creacion, onSaveEntity es más apropiado, 
                     // pero mantenemos la compatibilidad del prop.
                }
            }
        } else {
            if (onSaveEntity && entityData.id) {
                onSaveEntity(entityData as GenericEntity, itemsData);
            }
        }
        setModalState(prev => ({ ...prev, isOpen: false }));
    };

    const filtered = entities.filter(e =>
        e.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const columns: TableColumn<GenericEntity>[] = [
        { header: 'Código', accessor: 'code', sortable: true },
        { header: 'Descripción', accessor: 'description', sortable: true },
        {
            header: 'Estado',
            accessor: 'isActive',
            render: (val: boolean) => (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-normal ${val ? 'bg-[#dbeefe] text-[#0e79fd]' : 'bg-red-50 text-red-600'}`}>
                    {val ? 'Activo' : 'Inactivo'}
                </span>
            )
        },
        {
            header: 'Acciones',
            accessor: 'id',
            align: 'center',
            render: (_, row: GenericEntity) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSelectForEdit(row);
                    }}
                    className="inline-flex items-center justify-center text-[#0e79fd] hover:text-blue-800 transition-colors"
                    title="Editar"
                    disabled={isLoadingItems}
                >
                    <PencilSquareIcon className="h-5 w-5" />
                </button>
            )
        }
    ];

    return (
        <LayoutContent
            title={title}
            toolbarEndContent={
                <div className="flex w-full sm:w-auto items-center gap-3">
                    <div className="w-full sm:w-64">
                        <Input
                            placeholder="Buscar entidad..."
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                            startIcon={<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />}
                        />
                    </div>
                    <Button onClick={handleCreateClick} variant="default">
                        Nueva Entidad
                    </Button>
                </div>
            }
        >
            <div className={`w-full h-full flex flex-col gap-4 ${className}`}>
                <Table
                    columns={columns}
                    data={filtered}
                    loading={isLoading || isLoadingItems}
                />
            </div>

            <GenericEntityModal
                isOpen={modalState.isOpen}
                mode={modalState.mode}
                entity={modalState.entity}
                initialItems={modalState.items}
                onClose={handleCloseModal}
                onSave={handleSaveModal}
                isLoading={isLoading}
            />
        </LayoutContent>
    );
};
